import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import AddressInput from '../components/address/AddressInput';
import { getApiBaseUrl } from '../apiBase';
import { getActiveSchoolId } from '../utils/activeSchool';
import { getSchoolForSession } from '../utils/schoolHelper';
import { useTranslation } from 'react-i18next';

const cardStyle = { background: '#fff', borderRadius: '12px', border: '1px solid var(--sp-gray-100)', padding: '16px', marginBottom: '14px' };
const inputStyle = { width: '100%', border: '1px solid #e5e7eb', borderRadius: '9px', padding: '9px 10px', fontSize: '14px', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle };
const primaryBtn = { background: '#2563eb', color: '#fff', border: 'none', borderRadius: '9px', padding: '10px 16px', cursor: 'pointer', fontWeight: 600 };
const dangerBtn = { background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '9px', padding: '8px 12px', cursor: 'pointer', fontSize: 13 };

const emptyRoomForm = {
    name: '', type: 'owned', address: '', hourlyRate: '',
    isSchoolAddress: true, lat: null, lng: null
};

export default function SchoolVenuesPage() {
    const { t } = useTranslation(['schoolVenues', 'common']);
    const navigate = useNavigate();
    const [school, setSchool] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [roomForm, setRoomForm] = useState(emptyRoomForm);
    const [showCreate, setShowCreate] = useState(false);

    const token = localStorage.getItem('token');
    const api = useMemo(() => axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const schoolRes = await getSchoolForSession(api);
                const s = schoolRes.data;
                if (cancelled) return;
                setSchool(s);
                const roomsRes = await api.get('/room', { params: { schoolId: s.id } });
                if (cancelled) return;
                setRooms(roomsRes.data || []);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.message || t('schoolVenues:loadError'));
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [api]);

    async function load() {
        setLoading(true);
        setError('');
        try {
            const schoolRes = await getSchoolForSession(api);
            const s = schoolRes.data;
            setSchool(s);
            const roomsRes = await api.get('/room', { params: { schoolId: s.id } });
            setRooms(roomsRes.data || []);
        } catch (e) {
            setError(e.response?.data?.message || t('schoolVenues:loadError'));
        }
        setLoading(false);
    }

    async function handleCreateRoom(e) {
        e.preventDefault();
        if (!school?.id) return;
        try {
            await api.post('/room', {
                name: roomForm.name,
                type: roomForm.type,
                address: roomForm.isSchoolAddress ? '' : (roomForm.address || ''),
                addressFull: roomForm.isSchoolAddress ? null : (roomForm.address || ''),
                isSchoolAddress: roomForm.isSchoolAddress,
                lat: roomForm.isSchoolAddress ? null : roomForm.lat,
                lng: roomForm.isSchoolAddress ? null : roomForm.lng,
                hourlyRate: roomForm.type === 'rented' && roomForm.hourlyRate ? parseFloat(roomForm.hourlyRate) : null
            }, { params: { schoolId: school.id } });
            setRoomForm(emptyRoomForm);
            setShowCreate(false);
            await load();
        } catch (err) {
            setError(err.response?.data?.message || t('schoolVenues:createError'));
        }
    }

    async function handleDeactivate(id, name) {
        if (!window.confirm(t('schoolVenues:deactivateConfirm', { name }))) return;
        try {
            await api.post(`/room/${id}/deactivate`);
            await load();
        } catch (err) {
            setError(err.response?.data?.message || t('schoolVenues:deactivateError'));
        }
    }

    const activeRooms = rooms.filter(r => r.isActive);
    const inactiveRooms = rooms.filter(r => !r.isActive);

    return (
        <OwnerPageLayout
            title={t('schoolVenues:title')}
            subtitle={t('schoolVenues:subtitle')}
            maxWidth={900}
            actions={<Link to="/schedule" className="sp-btn sp-btn--ghost">{t('schoolVenues:backSchedule')}</Link>}
        >
                {error && <p style={{ color: '#b91c1c', marginTop: 12 }}>{error}</p>}

                <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" style={primaryBtn} onClick={() => setShowCreate(v => !v)}>
                        {showCreate ? t('schoolVenues:hideForm') : t('schoolVenues:newVenue')}
                    </button>
                </div>

                {showCreate && (
                    <form onSubmit={handleCreateRoom} style={{ ...cardStyle, marginTop: 12, background: '#f8fafc' }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>{t('schoolVenues:formTitle')}</h3>
                        <div style={{ display: 'grid', gap: 10 }}>
                            <input placeholder={t('schoolVenues:namePlaceholder')} value={roomForm.name}
                                onChange={e => setRoomForm({ ...roomForm, name: e.target.value })} required style={inputStyle} />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <select value={roomForm.type} onChange={e => setRoomForm({ ...roomForm, type: e.target.value })} style={selectStyle}>
                                    <option value="owned">{t('schoolVenues:owned')}</option>
                                    <option value="rented">{t('schoolVenues:rented')}</option>
                                </select>
                                {roomForm.type === 'rented' ? (
                                    <input type="number" min="0" step="0.01" placeholder={t('schoolVenues:ratePlaceholder')} value={roomForm.hourlyRate}
                                        onChange={e => setRoomForm({ ...roomForm, hourlyRate: e.target.value })} style={inputStyle} />
                                ) : (
                                    <input disabled value={t('schoolVenues:rateNotNeeded')} style={{ ...inputStyle, background: '#f1f5f9', color: '#94a3b8' }} />
                                )}
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                <input type="radio" checked={roomForm.isSchoolAddress}
                                    onChange={() => setRoomForm({ ...roomForm, isSchoolAddress: true, address: '', lat: null, lng: null })} />
                                {t('schoolVenues:schoolAddress')}
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                <input type="radio" checked={!roomForm.isSchoolAddress}
                                    onChange={() => setRoomForm({ ...roomForm, isSchoolAddress: false })} />
                                {t('schoolVenues:otherAddress')}
                            </label>
                            {!roomForm.isSchoolAddress && (
                                <AddressInput
                                    address={roomForm.address}
                                    lat={roomForm.lat}
                                    lng={roomForm.lng}
                                    city={school?.city || ''}
                                    country={school?.country || ''}
                                    schoolId={school?.id}
                                    inputStyle={inputStyle}
                                    onChange={(v) => setRoomForm({
                                        ...roomForm,
                                        address: v.address || '',
                                        lat: v.lat ?? null,
                                        lng: v.lng ?? null
                                    })}
                                />
                            )}
                            <button type="submit" style={primaryBtn}>{t('schoolVenues:saveVenue')}</button>
                        </div>
                    </form>
                )}

                {loading && <p style={{ marginTop: 16 }}>{t('common:loading')}</p>}

                {!loading && activeRooms.length === 0 && (
                    <p style={{ color: '#64748b', marginTop: 16 }}>{t('schoolVenues:emptyActive')}</p>
                )}

                {activeRooms.map(r => (
                    <div key={r.id} style={cardStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                                <h3 style={{ margin: 0 }}>{r.name}</h3>
                                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                                    {r.type === 'rented' ? t('schoolVenues:rentLine', { rate: r.hourlyRate || 0 }) : t('schoolVenues:owned')}
                                    {r.address ? ` · ${r.address}` : ''}
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button type="button" style={primaryBtn}
                                    onClick={() => navigate(`/school/venues/${r.id}`)}>
                                    {t('schoolVenues:orgsKkm')}
                                </button>
                                <button type="button" style={dangerBtn} onClick={() => handleDeactivate(r.id, r.name)}>
                                    {t('schoolVenues:deactivate')}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                {inactiveRooms.length > 0 && (
                    <>
                        <h3 style={{ fontSize: 15, color: '#64748b', marginTop: 20 }}>{t('schoolVenues:inactiveTitle')}</h3>
                        {inactiveRooms.map(r => (
                            <div key={r.id} style={{ ...cardStyle, opacity: 0.7 }}>
                                <span>{r.name}</span>
                                <span style={{ marginLeft: 8, fontSize: 12, color: '#94a3b8' }}>{t('schoolVenues:inactiveBadge')}</span>
                            </div>
                        ))}
                    </>
                )}
        </OwnerPageLayout>
    );
}
